import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Flex, Text } from '@chakra-ui/react'
import { Location, Region } from '@leid/api-locations'
import sortByDistance from 'sort-by-distance'

import { ItemApi } from 'appTypes/items'
import { CartFormValues } from 'appTypes/orders'
import { ButtonText } from 'components/ui/Buttons/Button'
import apiClient from 'helpers/clients/apiClient'

import RelatedProductCard from './RelatedProductCard'

type Props = {
  product: ItemApi
  products: ItemApi[]
  values: CartFormValues
  regions: Region[]
  productLocations: Location[]
}

const RelatedProducts = ({ product, products, values, regions, productLocations }: Props): JSX.Element => {
  const [areAllShown, setAreAllShown] = useState(false)
  const [allLocations, setAllLocations] = useState<Location[] | undefined>(undefined)
  const [closestLocations, setClosestLocations] = useState<Location[]>([])

  const { t } = useTranslation('common')

  useEffect(() => {
    if (window.innerWidth >= 1023) {
      setAreAllShown(true)
    }
  }, [])

  useEffect(() => {
    getClosestLocations(values.selectedLocationId, values.selectedRegionId)
  }, [values.selectedLocationId, values.selectedRegionId, product.id])

  const getClosestLocations = async (locationId: number | undefined, regionId: number | undefined) => {
    if (!locationId && !regionId) {
      setClosestLocations([])
      return
    }

    let lat: number | undefined = undefined
    let long: number | undefined = undefined

    if (locationId) {
      const selectedLocation = productLocations.find((s) => s.id === locationId)
      if (!selectedLocation) {
        setClosestLocations([])
        return
      }
      lat = selectedLocation.lat
      long = selectedLocation.long
    } else {
      if (regionId) {
        const selectedRegion = regions.find((s) => s.id === regionId)
        if (!selectedRegion) {
          setClosestLocations([])
          return
        }
        lat = selectedRegion.lat
        long = selectedRegion.long
      }
    }

    let locations = allLocations
    if (!locations) {
      const response = await apiClient.getRealLocations({ active: true })
      locations = response.data?.rows ?? []

      if (response.success && response.data) {
        setAllLocations(locations)
      }
    }

    const closeLocations = sortByDistance({ long: long, lat: lat }, locations, { yName: 'lat', xName: 'long' }).filter(
      (s) => s.distance <= 50,
    )
    setClosestLocations(closeLocations as Location[])
  }

  return (
    <Box width="100%" marginBottom="30px">
      <Flex marginTop="36px" justifyContent="space-between" alignItems="center">
        <Text fontSize="sm">{t('relatedProducts')}</Text>
        {products.length > 1 && (
          <Box display={{ lg: 'none' }}>
            <ButtonText onClick={() => setAreAllShown(!areAllShown)} href={undefined}>
              {t(areAllShown ? 'showLess' : 'showAll')}
            </ButtonText>
          </Box>
        )}
      </Flex>
      <Box
        marginTop="12px"
        display="grid"
        gridGap="20px"
        gridTemplateColumns={{ base: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' }}
      >
        {products?.slice(0, 2).map((product) => (
          <RelatedProductCard key={product.id} product={product} values={values} locations={closestLocations} />
        ))}

        {areAllShown && (
          <>
            {products?.slice(2, products.length).map((product) => (
              <RelatedProductCard key={product.id} product={product} values={values} locations={closestLocations} />
            ))}
          </>
        )}
      </Box>
    </Box>
  )
}

export default RelatedProducts
